import React, { useState, useRef, useEffect } from 'react';
import { useBotChat } from '../../hooks/useBotChat';
import './BotChat.css';

/**
 * BotChat Component - Embeddable chat interface for Peter Bot
 * @param {string} conversationId - Conversation ID
 * @param {string} token - Chat token for authentication
 * @param {string} apiUrl - Peter Bot API URL
 * @param {function} onClose - Callback when chat is closed
 * @param {boolean} hideHeader - Hide the header (for floating widget)
 */
export default function BotChat({ conversationId, token, apiUrl, onClose, hideHeader = false }) {
  const { messages, loading, sending, error, status, sendMessage } = useBotChat(conversationId, token, apiUrl);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || sending || status === 'resolved') return;

    try {
      await sendMessage(inputValue);
      setInputValue('');
    } catch (err) {
      console.error('Error sending message:', err);
    }
  };

  if (loading) {
    return (
      <div className="peter-bot-chat">
        <div className="peter-bot-chat-loading">
          <div className="spinner"></div>
          <p>Loading conversation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="peter-bot-chat">
        <div className="peter-bot-chat-error">
          <p>Error: {error}</p>
          <p>Please check your connection and try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="peter-bot-chat">
      {!hideHeader && (
        <div className="peter-bot-chat-header">
          <div className="peter-bot-chat-header-info">
            <h3>{process.env.REACT_APP_BOT_NAME || 'Peter Bot'}</h3>
            {status === 'resolved' && (
              <span className="peter-bot-status-badge resolved">Resolved</span>
            )}
          </div>
          {onClose && (
            <button className="peter-bot-close-btn" onClick={onClose}>×</button>
          )}
        </div>
      )}

      <div className="peter-bot-chat-messages">
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`peter-bot-message ${msg.role === 'bot' ? 'bot' : 'user'}`}
          >
            <div className="peter-bot-message-content">
              {msg.content}
            </div>
            <div className="peter-bot-message-time">
              {new Date(msg.timestamp).toLocaleTimeString()}
            </div>
          </div>
        ))}
        {sending && (
          <div className="peter-bot-message bot">
            <div className="peter-bot-message-content">
              <div className="peter-bot-typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {status === 'resolved' ? (
        <div className="peter-bot-chat-resolved">
          <p>This conversation has been resolved. Thank you!</p>
        </div>
      ) : (
        <form className="peter-bot-chat-input-form" onSubmit={handleSend}>
          <input
            type="text"
            className="peter-bot-chat-input"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Type your message..."
            disabled={sending}
          />
          <button
            type="submit"
            className="peter-bot-chat-send-btn"
            disabled={!inputValue.trim() || sending}
          >
            Send
          </button>
        </form>
      )}
    </div>
  );
}

